import { useFormContext, useWatch } from "react-hook-form";
import { CotationFormData } from "@/types/forms/home/cotationFormData.form";
import CotationCard from "./CotationCard";
import { Info } from "lucide-react";
import { GetCustomsV1Response, GetCustomV1Response } from "@/types/api/api-types";
import { ProductElem } from "./ProductCard/productElem";
import { ControlledCustomsSelect } from "@/components/ui/customs/controlled-customs-select";
import { Button } from "@/components/ui/button";

type ProductCardProps = {
  customs: GetCustomsV1Response | undefined;
  selectedCustom?: GetCustomV1Response;
  isCustomerPage?: boolean;
  resetCount: number;
};

export const ProductCard = ({ customs, selectedCustom, resetCount }: ProductCardProps) => {
  const { control, trigger, setValue } = useFormContext<CotationFormData>();
  const products = useWatch({
    control,
    name: "products",
  });

  const handleAppendProduct = async () => {
    const productTrigger = await trigger("products");
    if (!productTrigger) return;

    // Set isNew to false for the last product if it exists and is new
    const products = control._formValues.products;
    const lastIndex = products.length - 1;
    if (lastIndex >= 0 && products[lastIndex]?.isNew) {
      setValue(`products.${lastIndex}.isNew`, false);
    }

    setValue("products", [
      ...products,
      {
        bottleQuantity: undefined as unknown as number,
        value: undefined as unknown as number,
        volume: 0.75,
        isNew: true,
      },
    ]);
  };

  const handleRemoveProduct = (index: number) => {
    if (products.length <= 1) return;
    setValue(
      "products",
      products.filter((_, i) => i !== index),
    );
  };

  return (
    <CotationCard key={resetCount}>
      <div className="flex w-full flex-1 flex-col gap-4">
        <ControlledCustomsSelect
          customs={customs || []}
          control={control}
          name="country"
          label="Pays de destination"
        />
        {selectedCustom?.customAdditionalRequests.map((request) => (
          <div
            key={request.id}
            className="flex flex-row gap-4 rounded-lg border border-orange-300 bg-orange-50 p-2"
          >
            <Info className="text-orange-700" />
            <p className="font-semibold">
              {request.title}
              <span className="text-sm font-normal">{" " + request.description}</span>
            </p>
          </div>
        ))}
        {products.map((_product, index) => (
          <ProductElem
            key={`product-${index}`}
            index={index}
            remove={handleRemoveProduct}
            control={control}
            error={control._formState.errors.products?.[index] !== undefined}
          />
        ))}
        <Button
          onClick={handleAppendProduct}
          variant="tertiary"
          className="h-auto self-end! text-wrap whitespace-normal"
        >
          Ajouter un produit supplémentaire
        </Button>
      </div>
    </CotationCard>
  );
};
